import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Box, Typography, Card, CardContent, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { usersAPI } from "../../services/api";

export default function TeamManagement() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newTeamName, setNewTeamName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTeam, setDeleteTeam] = useState(null);

  useEffect(() => { loadTeams(); }, []);

  const loadTeams = async () => {
    try {
      const res = await usersAPI.getTeams();
      setTeams(res.data);
    } catch { setError("Failed to load teams."); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newTeamName.trim()) { setError("Team name is required."); return; }
    setCreating(true);
    try {
      await usersAPI.createTeam({ team_name: newTeamName.trim() });
      setSuccess(`Team "${newTeamName}" created successfully.`);
      setNewTeamName("");
      loadTeams();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Failed to create team.");
    } finally { setCreating(false); }
  };

  const handleDelete = async () => {
    try {
      await usersAPI.deleteTeam(deleteTeam.team_id);
      setSuccess(`Team "${deleteTeam.team_name}" deleted.`);
      setDeleteOpen(false);
      setDeleteTeam(null);
      loadTeams();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Failed to delete team. It may have users or learning paths assigned.");
      setDeleteOpen(false);
    }
  };

  if (user?.role !== "admin") return <Alert severity="error">Admin access required.</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="#1a237e" mb={3}>Team Management</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2} color="#1a237e">Add New Team</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth label="Team Name" value={newTeamName}
              onChange={e => setNewTeamName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
              placeholder="e.g. Operations, Sales, Support"
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}
              disabled={creating || !newTeamName.trim()}
              style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff", whiteSpace: "nowrap" }}>
              {creating ? <CircularProgress size={20} color="inherit" /> : "Add Team"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2} color="#1a237e">Existing Teams</Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                    <TableCell><b>Team Name</b></TableCell>
                    <TableCell><b>Team ID</b></TableCell>
                    <TableCell><b>Action</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teams.map(t => (
                    <TableRow key={t.team_id} hover>
                      <TableCell><Typography variant="body1" fontWeight={600}>{t.team_name}</Typography></TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary" fontFamily="monospace">{t.team_id}</Typography></TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" color="error"
                          onClick={() => { setDeleteTeam(t); setDeleteOpen(true); }}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle fontWeight={700} color="error.main">⚠️ Delete Team</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <b>{deleteTeam?.team_name}</b>?</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            This will fail if any users or learning paths are assigned to this team.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
